import { Box, Typography } from '@mui/material'
import { Prescription } from '../../../../types/models/Presc'
import { localStorageWrapper } from '../../../../helpers/localStorage'
import { colorTokens } from '../../../../shared/styles/theme'
import { FONT_TABULAR, typeScale } from '../../../../shared/styles/typography'
import { isQrOnly } from '../list/queue'
import { showsSendButton } from '../remoteInput/model'
import { useRemoteInput } from '../remoteInput/useRemoteInput'
import { SendToReceiptComputer } from './SendToReceiptComputer'

/**
 * 項目ペインの末尾の 1 行。**左が受電キー、右が送信**(04-apps/mamoru.md「レセコンに送信」)。
 *
 * サポートコード = Request ID の末尾4桁。店舗 → コールセンター → 情報システム部 → mediLab の
 * 受電フローで事象を特定する唯一のキーで、店舗配布シートと受電台本が「読取結果画面 右下の末尾4桁」を
 * 逐語で指すため、位置(画面右下)と呼称を紙に合わせる。末尾4桁が衝突したときの突合に要るので
 * **全 ID は末尾4桁の下**に置き、空いた右側を送信に使う。
 *
 * コピーボタンは付けない(受電では口頭で読み上げるため)。
 *
 * **送信ボタンを出す条件はここ 1 か所**(`showsSendButton`)。
 * - **JAHIS QR 直読の行には出さない** — backend は QR 直読で `QRImage` を作らず
 *   (`record_saver.py` は QR の中身をそのまま `SentFile` にして送る)、`send_latest_jahis` は
 *   最新の `QRImage` が無いと必ず失敗する。取り込み時点で既に届いてもいる。
 * - 遠隔入力に乗った紙は店の設定(`show_save_csv_button`)を見ない(頼まれた紙は常に出し、
 *   自店の紙は依頼中・受領の間だけ隠す)。backend の門も依頼の行がある紙を通す。
 * - それ以外は店舗設定 `show_save_csv_button` が真のときだけ(ADR-0033)。backend も
 *   `when_to_save_csv` の bit1 が無ければ 400 を返すので、出す条件を backend と同じ根に置く。
 */
export const DetailFooter = ({
  prescription,
}: {
  prescription: Prescription
}) => {
  const { lookup } = useRemoteInput()
  const requestId = prescription.ocr_request_event_id
  if (!requestId) return null

  const storeHasButton = !!localStorageWrapper.getItem('show_save_csv_button')
  const showsSend =
    !isQrOnly(prescription) &&
    showsSendButton(lookup(requestId), storeHasButton)

  return (
    <Box
      sx={{
        mt: 3,
        pt: 1.5,
        borderTop: `1px solid ${colorTokens.border.faint}`,
        display: 'flex',
        alignItems: 'center',
        gap: 2,
      }}
    >
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1 }}>
          <Typography sx={{ fontSize: 12, color: colorTokens.fg.muted }}>
            サポートコード
          </Typography>
          {/* 見出し級の字にしない。読み上げるだけの参照値なので label(13/500)。 */}
          <Typography sx={{ ...typeScale.label, ...FONT_TABULAR }}>
            {requestId.slice(-4)}
          </Typography>
        </Box>
        <Typography sx={{ fontSize: 11, color: colorTokens.fg.faint }} noWrap>
          {requestId}
        </Typography>
      </Box>
      {showsSend && <SendToReceiptComputer prescriptionId={prescription.id} />}
    </Box>
  )
}
